import React, { forwardRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NewsletterService } from "@/services/newsletterService";
import { Loader2, CheckCircle, AlertCircle, Mail, Star, Sparkles, Bell } from "lucide-react";

const Newsletter = forwardRef<HTMLDivElement>((props, ref) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await NewsletterService.subscribe({ 
        email: email.trim(),
        source: 'landing-page'
      });

      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        setEmail(""); // Clear form on success
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Something went wrong. Please try again later.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div ref={ref} className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-16 sm:py-20 overflow-hidden" id="newsletter">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Circles */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-jamaica-yellow/10 rounded-full animate-bounce delay-100"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-jamaica-green/10 rounded-full animate-pulse delay-300"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-ping delay-500"></div>
        <div className="absolute bottom-32 right-10 w-24 h-24 bg-jamaica-green/8 rounded-full animate-bounce delay-700"></div>
        
        {/* Animated Gradient Blobs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-r from-jamaica-green/5 to-jamaica-yellow/5 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-l from-jamaica-yellow/5 to-jamaica-green/5 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1500"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="group relative">
          {/* Main Card with Glass Effect */}
          <div className="relative backdrop-blur-sm bg-white/70 rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-12 xl:p-16 shadow-2xl border border-white/20 transform-gpu landing-newsletter-container">
            
            {/* Animated Border Gradient */}
            <div className="absolute inset-0 rounded-2xl lg:rounded-3xl bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green p-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="rounded-2xl lg:rounded-3xl bg-white/90 w-full h-full"></div>
            </div>
            
            {/* Content */}
            <div className="relative z-10 space-y-8 lg:space-y-0 lg:flex lg:items-center lg:justify-between lg:gap-8 xl:gap-12">
              
              {/* Left Content Section */}
              <div className="max-w-2xl text-center lg:text-left space-y-4 lg:space-y-6">
                {/* Icon and Badge */}
                <div className="flex flex-col sm:flex-row justify-center lg:justify-start items-center gap-3 mb-4">
                  <div className="relative">
                    <div className="w-14 h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-lg transform rotate-3 landing-feature-icon">
                      <Mail className="w-7 h-7 lg:w-8 lg:h-8 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 lg:w-6 lg:h-6 bg-jamaica-yellow rounded-full flex items-center justify-center animate-pulse">
                      <Sparkles className="w-2.5 h-2.5 lg:w-3 lg:h-3 text-white" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 lg:px-4 lg:py-2 rounded-full border border-jamaica-green/20">
                    <span className="text-xs lg:text-sm font-semibold text-jamaica-black flex items-center gap-1">
                      <Star className="w-3 h-3 lg:w-4 lg:h-4 text-jamaica-yellow fill-current" />
                      Insider Updates
                    </span>
                  </div>
                </div>

                {/* Headline */}
                <div className="space-y-2 lg:space-y-3">
                  <h2 className="font-bold text-gray-900 leading-tight text-xl sm:text-2xl lg:text-3xl xl:text-4xl">
                    <span className="bg-gradient-to-r from-jamaica-green to-jamaica-green/80 bg-clip-text text-transparent p-1">
                      Join Our Jamaican Family
                    </span>
                  </h2>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base lg:text-lg max-w-lg mx-auto lg:mx-0">
                    Get exclusive offers, new product alerts, and authentic Jamaican vibes delivered straight to your inbox.
                  </p>
                  <div className="inline-flex items-center gap-1 text-jamaica-green font-medium text-sm sm:text-base justify-center lg:justify-start">
                    <Bell className="w-4 h-4" />
                    Be the first to know about:
                  </div>
                </div>

                {/* Benefits List - Progressive Responsive Layout */}
                <div className="flex justify-center lg:justify-start">
                  <div className="grid grid-cols-1 md:grid-cols-2 text-gray-600 gap-2 lg:gap-3 text-xs sm:text-sm lg:text-sm">
                    <div className="flex items-start justify-start gap-3 py-1">
                      <div className="w-2 h-2 bg-jamaica-green rounded-full animate-pulse flex-shrink-0 mt-1.5"></div>
                      <span className="text-left">New product launches</span>
                    </div>
                    <div className="flex items-start justify-start gap-3 py-1">
                      <div className="w-2 h-2 bg-jamaica-yellow rounded-full animate-pulse delay-100 flex-shrink-0 mt-1.5"></div>
                      <span className="text-left">Exclusive deals</span>
                    </div>
                    <div className="flex items-start justify-start gap-3 py-1">
                      <div className="w-2 h-2 bg-jamaica-green rounded-full animate-pulse delay-200 flex-shrink-0 mt-1.5"></div>
                      <span className="text-left">Latest trends & updates</span>
                    </div>
                    <div className="flex items-start justify-start gap-3 py-1">
                      <div className="w-2 h-2 bg-jamaica-yellow rounded-full animate-pulse delay-300 flex-shrink-0 mt-1.5"></div>
                      <span className="text-left">Company news & features</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right Form Section */}
              <div className="w-full max-w-md mx-auto lg:w-auto lg:flex-shrink-0 lg:ml-0 lg:max-w-none">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-xl border border-white/40 transform landing-newsletter-form p-4 sm:p-5 lg:p-6">
                  <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                    <div className="relative group">
                      <label htmlFor="email-address" className="sr-only">Email address</label>
                      <div className="relative">
                        <input
                          id="email-address"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email address"
                          disabled={isLoading}
                          className="w-full border-2 border-gray-200 shadow-sm placeholder-gray-400 focus:ring-2 focus:ring-jamaica-green/50 focus:border-jamaica-green/50 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:border-jamaica-green/50 group-hover:shadow-md px-4 py-3 sm:px-5 sm:py-4 text-sm sm:text-base"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none pr-3 sm:pr-4">
                          <Mail className="text-gray-400 group-hover:text-jamaica-green transition-colors duration-300 w-4 h-4 sm:w-5 sm:h-5" />
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white disabled:opacity-50 font-semibold rounded-xl shadow-lg disabled:hover:transform-none disabled:hover:scale-100 landing-newsletter-button px-6 py-3 sm:px-8 sm:py-4 text-sm sm:text-base"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 animate-spin h-4 w-4 sm:h-5 sm:w-5" />
                          <span>Joining the family...</span>
                        </>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                          Join the Family
                          <Star className="fill-current w-3 h-3 sm:w-4 sm:h-4" />
                        </span>
                      )}
                    </Button>
                  </form>
                  
                  {/* Success/Error Message */}
                  {message && (
                    <div className={`mt-3 sm:mt-4 p-3 sm:p-4 rounded-xl flex items-start gap-2 sm:gap-3 text-xs sm:text-sm transition-all duration-500 transform ${
                      message.type === 'success' 
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 border-2 border-green-200 shadow-green-100 shadow-lg' 
                        : 'bg-gradient-to-r from-red-50 to-rose-50 text-red-800 border-2 border-red-200 shadow-red-100 shadow-lg'
                    }`}>
                      <div className="flex-shrink-0 mt-0.5">
                        {message.type === 'success' ? (
                          <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 animate-pulse" />
                        ) : (
                          <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 animate-pulse" />
                        )}
                      </div>
                      <span className="leading-relaxed font-medium">{message.text}</span>
                    </div>
                  )}
                  
                  {/* Privacy Notice */}
                  <p className="mt-3 sm:mt-4 text-xs text-gray-500 text-center leading-relaxed">
                    🔒 We respect your privacy. Read our{" "}
                    <Link to="/privacy" className="font-medium underline text-jamaica-green hover:text-jamaica-green/80 transition-colors">
                      Privacy Policy
                    </Link>
                    . Unsubscribe anytime.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

Newsletter.displayName = "Newsletter";

export default Newsletter;
